/**
 * @file utils/Enum.js 通用枚举类
 * @author zhangzhe(zhangzhe@baidu.com)
 */

/**
 * 通用枚举类，用于生成一个枚举对象
 *
 * 枚举是一类数值的集合，枚举中的每一项包括3个属性：
 * - `value`：对应原始的数字值
 * - `alias`：枚举项在业务中的别名，通常使用大写字母和下划线组成的常量表达方式
 * - `text`：该项显示时的文字
 *
 * 使用本类，可以简单地创建一个枚举对象，如：
 *
 * var Status = new Enum(
 *     {alias: 'NORMAL', text: '正常', value: 'normal'},
 *     {alias: 'DISABLED', text: '禁用', value: 'disabled'},
 *     {alias: 'DELETED', text: '已删除', value: 'deleted'}
 * );
 *
 *
 * 枚举会维护2个映射，分别为`value`至`alias`及`alias`至`value`，因此可以简单地通过属性访问的方式获取`alias`或`value`：
 *
 * if (user.status === Status.DELETED) {
 *     warn('该用户已经删除');
 * }
 *
 * @class Enum
 */

import _ from 'lodash';

interface EnumItem {
    alias: string;
    text: string;
    value: string | number;
    className?: string;
}

export interface EnumStatic {
    [x: string]: any;
}

export interface EnumConstructor {
    new (...args: any[]): EnumStatic;
    [x: string]: any;
}

const valueIndex = Symbol('valueIndex');
const textIndex = Symbol('textIndex');
const aliasIndex = Symbol('aliasIndex');

const Enum: EnumConstructor = class {

    constructor(...args: any[]) {
        this[valueIndex] = {};
        this[textIndex] = {};
        this[aliasIndex] = {};

        for (let i = 0, len = args.length; i < len; i++) {
            const item = args[i];
            if (item.value === null || item.value === undefined) {
                item.value = i;
            }
            this.addItem(item);
        }
    }

    /**
     * 为当前枚举对象添加一个枚举项
     *
     * @param {EnumItem} item 待添加的枚举项
     */
    addItem(item: EnumItem) {
        this[item.alias] = item.value;
        this[item.value] = item.alias;

        this[valueIndex][item.value] = item;
        this[textIndex][item.text] = item;
        this[aliasIndex][item.alias] = item;
    }

    /**
     * 根据数值获取枚举项，如果不存在则返回空对象
     *
     * @param {number | string} value 数值
     * @return {meta.EnumItem} 对应的枚举项
     * @override
     */
    fromValue(value: string | number) {
        return this[valueIndex][value] || {};
    }

    /**
     * 根据别名获取枚举项，如果不存在则返回空对象
     *
     * @param {string} alias 别名
     * @return {meta.EnumItem} 对应的枚举项
     * @override
     */
    fromAlias(alias: string) {
        return this[aliasIndex][alias] || {};
    }

    /**
     * 根据文字获取枚举项
     *
     * @param {string} text 文字
     * @return {meta.EnumItem} 对应的枚举项
     */
    fromText(text: string) {
        return this[textIndex][text] || {};
    }

    /**
     * 根据数值获取对应枚举项的文字
     *
     * @param {number} value 数值
     * @return {string} 对应的文字
     */
    getTextFromValue(value: string | number) {
        return this.fromValue(value).text;
    }

    /**
     * 根据文字获取对应枚举项的文字
     *
     * @param {string} alias 文字
     * @return {string} 对应的文字
     */
    getTextFromAlias(alias: string) {
        return this.fromAlias(alias).text;
    }

    /**
     * 根据数值获取对应枚举项的数值
     *
     * @param {string} alias 数值
     * @return {number} 对应的数值
     */
    getValueFromAlias(alias: string) {
        return this.fromAlias(alias).value;
    }

    /**
     * 根据文字获取对应枚举项的数值
     *
     * @param {string} text 文字
     * @return {number} 对应的数值
     */
    getValueFromText(text: string) {
        return this.fromText(text).value;
    }

    /**
     * 根据数值获取对应枚举项的别名
     *
     * @param {number} value 数值
     * @return {string} 对应的别名
     */
    getAliasFromValue(value: string | number) {
        return this.fromValue(value).alias;
    }

    /**
     * 根据文字获取对应枚举项的别名
     *
     * @param {string} text 文字
     * @return {string} 对应的别名
     */
    getAliasFromText(text: string) {
        return this.fromText(text).alias;
    }

    /**
     * 将枚举对象转换成json格式的对象
     *
     * @return {json} 对应json格式的对象
     */
    toJson() {
        const json = {};
        _.forEach(this[valueIndex], (item, value) => {
            json[value] = item.text;
        });
        return json;
    }

    /**
     * 将枚举对象转换成数组，常用于下拉选择控件之类的数据源
     *
     * @return {Array} 枚举对象对应的数组
     */
    toArray() {
        return _.map(this[valueIndex], item => ({...item, ...{label: item.text}}));
    }

};

export default Enum;
